import type { SOSOfficer } from "@/components/DefaultSOSTable";

/** A row of the support page these officers are drawn from, read off the PDF. */
export interface SupportOfficerInfo {
  sn: number;
  name: string;
  /** Counter written against the name, replacing the position's first counter. */
  counter?: number | null;
  counter_label?: string;
}

/** The S/N a placeholder name like "SN2" stands for, or null if it isn't one. */
function placeholderSn(name: string): number | null {
  const match = /^SN(\d+)$/.exec(name.trim());
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Fill in from the PDF who is actually on each position tonight.
 *
 * The rows here are positions on the train page (arrival) — that is what the "SN2"
 * placeholder names mean, and why the counters are fixed but the officers are not.
 * Night departure has no such positions: its OWC officers are read, with their real
 * counters and times, from the roster itself (utils/rosterSos.ts). The PDF says which positions were
 * filled and by whom, so a position nobody was written into comes back unticked
 * rather than generating an officer who is not on duty.
 *
 * A row's S/N is read off its placeholder name the first time this runs, then
 * carried on `supportSn` from there — because resolving the name to "JOHN TAN"
 * is what a match *does*, and a name-only lookup would go blind on the very
 * row it just filled in. Re-uploading a different PDF without this went
 * looking for "SN2" again, found "JOHN TAN" instead, and left the row exactly
 * as the first PDF had it — checked, and named for a shift that had moved on.
 *
 * Officers whose name was never a placeholder have been named by hand or by an
 * admin default, and are left exactly as they are.
 */
export function applySupportOfficers(
  officers: SOSOfficer[],
  support: SupportOfficerInfo[],
): SOSOfficer[] {
  if (support.length === 0) return officers;
  const byServiceNumber = new Map(
    support.map((o) => [o.sn, { ...o, name: o.name.trim() }]),
  );

  return officers.map((officer) => {
    const sn = officer.supportSn ?? placeholderSn(officer.name);
    if (sn === null) return officer;
    const found = byServiceNumber.get(sn);
    if (found === undefined) return officer;
    if (!found.name) return { ...officer, checked: false, supportSn: sn };

    // "---> DC 11" beside the name moves this officer off the counter their
    // position normally opens on. It replaces the first counter only — the rest
    // of the position's night still follows the template.
    const replacement = found.counter ? String(found.counter) : null;
    const counterRows = replacement
      ? officer.counterRows.map((row, index) =>
          index === 0 && row.counter !== replacement
            ? { ...row, counter: replacement, replacedFrom: row.counter }
            : row,
        )
      : officer.counterRows;

    return { ...officer, name: found.name, checked: true, counterRows, supportSn: sn };
  });
}
